// framework/client/components/Login.js
import { ComponentBase } from "../core/ComponentBase.js";
import { z } from "zod";

export class Login extends ComponentBase {
  constructor(config) {
    super(config);
    this.container = document.createElement("div");
    this.container.className = "login-container";
    this.formRenderer = null;
    this._boundHandleSubmit = this.handleSubmit.bind(this);
    this._boundHandleOAuthMessage = this.handleOAuthMessage.bind(this);
    this.isProcessingOAuth = false;
    this.emailLoginVisible = true;
    this.oauthWindows = new Map(); // Track open OAuth windows
  }

  async onShow() {
    if (await this.checkForOtlToken()) {
      return;
    }
    await this.checkForOAuthPayload();
    this.checkForOAuthError();

    // Set up postMessage listener for OAuth popups
    window.addEventListener('message', this._boundHandleOAuthMessage);
  }

  // async refresh() {
  //   if (await this.checkForOtlToken()) {
  //     return;
  //   }
  //   await this.checkForOAuthPayload();
  //   this.checkForOAuthError();
  // }

  async initialize() {
    this.sessionTokenHandler = this.appClient.sessionTokenHandler;
    const clientConfig = await this.sessionTokenHandler.getClientConfig();

    const formConfig = {
      labelMode: 'floating',
      gapClass: 'mb-2',
      ...clientConfig.auth?.login,
      fields: [
        { name: "identifier", type: "text", label: "Username or Email", required: true, autocomplete: "username" },
        { name: "password", type: "password", label: "Password", required: true, autocomplete: "current-password" }
      ],
      validation: { validateOnChange: true },
      title: '',
    };

    const loginSchema = z.object({
      identifier: z.string().min(1, { message: "Identifier is required" }),
      password: z.string().min(1, { message: "Password is required" }),
    });

    this.formRenderer = await this.createManagedComponent('GenericFormRenderer', null, {
      instanceId: `${this.config.instanceId}-form-renderer`,
      formId: 'loginForm',
      formConfig: formConfig,
      schema: loginSchema,
      compact: true
    });

    await super.initialize();
  }

  // NEW: Validate and handle OAuth postMessage from popup
  handleOAuthMessage(event) {
    // Validate message structure - only process messages intended for OAuth
    if (!event.data || typeof event.data !== 'object') {
      return;
    }

    // Ignore non-OAuth messages (such as internal Cloudflare Turnstile iframe messages)
    const { success, provider, error, type } = event.data;
    if (!provider && !type?.startsWith?.('oauth')) {
      return;
    }

    // CRITICAL: Validate origin for OAuth messages
    const expectedOrigin = window.location.origin;
    if (event.origin !== expectedOrigin) {
      this.log.warn(`Rejected OAuth message from unauthorized origin: ${event.origin}`);
      return;
    }

    // Close the popup window if it's tracked
    if (this.oauthWindows.has(provider)) {
      const popup = this.oauthWindows.get(provider);
      if (popup && !popup.closed) {
        popup.close();
      }
      this.oauthWindows.delete(provider);
    }

    if (success) {
      this.appClient.notifier.success(`Successfully connected ${provider} account`);
      // Trigger any necessary refresh
      this.appClient.emit('channel:connected', { provider });
    } else {
      if (error !== 'authentication_cancelled') {
        this.appClient.notifier.error(`Failed to connect ${provider} account`);
      }
    }
  }

  _createOAuthProviderButtons() {
    const oauthConfig = this.sessionTokenHandler.clientConfig?.auth?.oauth || {};
    const enabledProviders = Object.entries(oauthConfig).filter(([name, config]) => config.enabled && name.indexOf('channel') < 0);

    if (enabledProviders.length === 0) return null;

    const container = document.createElement('div');
    container.className = 'd-grid gap-2 mb-1';

    enabledProviders.forEach(([providerName, config]) => {
      const providerTitle = providerName.charAt(0).toUpperCase() + providerName.slice(1);
      const textContent = `Continue with ${providerTitle}`;

      const wrapper = document.createElement('div');
      wrapper.style.position = 'relative';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'btn btn-outline-secondary w-100';
      button.textContent = textContent;
      button.title = `Sign in with ${providerTitle}`;
      button.setAttribute('data-i18n', textContent);

      let logoUrl = '';
      if (providerName === 'google') {
        logoUrl = '/assets/icons/google-logo.svg';
      } else if (providerName === 'microsoft') {
        logoUrl = '/assets/icons/microsoft-logo.svg';
      }

      if (logoUrl) {
        const img = document.createElement('img');
        img.src = logoUrl;
        img.style.position = 'absolute';
        img.style.left = '1rem';
        img.style.top = '50%';
        img.style.transform = 'translateY(-50%)';
        img.style.height = '1.25em';
        img.style.width = '1.25em';
        wrapper.appendChild(img);
      } else {
        button.classList.add('d-flex', 'align-items-center', 'justify-content-center');
        button.innerHTML = `<i class="bi bi-${providerName} me-2"></i> ${textContent}`;
      }

      button.addEventListener('click', async () => {
        // Prevent multiple simultaneous OAuth attempts for same provider
        if (this.oauthWindows.has(providerName)) {
          const existingWindow = this.oauthWindows.get(providerName);
          if (existingWindow && !existingWindow.closed) {
            existingWindow.focus();
            return;
          }
        }

        try {
          await this.startOAuthLogin(providerName);
        } catch (error) {
          this.log.error(`OAuth login failed for ${providerName}:`, error);
          this.appClient.notifier.error(`Could not complete ${providerName} login.`);
          await this.render();
        }
      });

      wrapper.appendChild(button);
      container.appendChild(wrapper);
    });

    return container;
  }

  isNativeExternalOAuthAvailable() {
    if (typeof window === 'undefined') return false;
    if (window.electronAPI && typeof window.electronAPI.openExternalUrl === 'function') return true;
    if (window.Capacitor?.isNativePlatform?.()) return true;
    return window.parent && window.parent !== window;
  }

  async openExternalOAuthUrl(url) {
    if (window.electronAPI && typeof window.electronAPI.openExternalUrl === 'function') {
      return window.electronAPI.openExternalUrl(url);
    }

    const capacitorBrowser = window.Capacitor?.Plugins?.Browser || window.CapacitorBrowser;
    if (window.Capacitor?.isNativePlatform?.() && capacitorBrowser?.open) {
      await capacitorBrowser.open({ url });
      return { success: true };
    }

    if (window.parent && window.parent !== window) {
      return this.openExternalOAuthUrlViaParent(url);
    }

    return { success: false, error: 'No native external browser bridge is available.' };
  }

  openExternalOAuthUrlViaParent(url) {
    return new Promise((resolve) => {
      const requestId = `oauth-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const timeout = setTimeout(() => {
        window.removeEventListener('message', onMessage);
        resolve({ success: false, error: 'Timed out waiting for native browser bridge.' });
      }, 10000);

      const onMessage = (event) => {
        if (event.source !== window.parent || event.data?.type !== 'appbuilder:openExternalUrlResult' || event.data?.requestId !== requestId) {
          return;
        }

        clearTimeout(timeout);
        window.removeEventListener('message', onMessage);
        resolve(event.data.result || { success: true });
      };

      window.addEventListener('message', onMessage);
      window.parent.postMessage({ type: 'appbuilder:openExternalUrl', requestId, url }, '*');
    });
  }

  async startOAuthLogin(providerName) {
    if (!this.isNativeExternalOAuthAvailable()) {
      const result = await this.appClient.rpcClient.call('AuthRpc.getOAuthLoginUrl', { provider: providerName });
      if (result.url) {
        // Web and Capacitor keep the normal redirect-based OAuth behavior.
        window.location.href = result.url;
      }
      return;
    }

    const externalSession = await this.appClient.rpcClient.call('AuthRpc.createExternalOAuthLoginSession', { provider: providerName });
    if (!externalSession?.authUrl || !externalSession?.pollToken) {
      throw new Error('External OAuth session could not be created.');
    }

    const openResult = await this.openExternalOAuthUrl(externalSession.authUrl);
    if (openResult && openResult.success === false) {
      throw new Error(openResult.error || 'Could not open the system browser.');
    }

    this.renderLoadingState("Complete sign-in in your browser...");
    const sessionPayload = await this.pollExternalOAuthLogin(externalSession.pollToken, externalSession.expiresInMs);
    await this.appClient.sessionTokenHandler.processLoginSuccess(sessionPayload);
  }

  async pollExternalOAuthLogin(pollToken, expiresInMs = 10 * 60 * 1000) {
    const deadline = Date.now() + expiresInMs;

    while (Date.now() < deadline) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      const result = await this.appClient.rpcClient.call('AuthRpc.pollExternalOAuthLoginSession', { pollToken });

      if (result.status === 'pending') {
        continue;
      }

      if (result.status === 'complete' && result.sessionPayload) {
        return result.sessionPayload;
      }

      throw new Error(result.error || `OAuth login ${result.status || 'failed'}.`);
    }

    throw new Error('OAuth login timed out.');
  }

  _createSeparator() {
    const separator = document.createElement('div');
    separator.className = 'd-flex align-items-center my-1';
    separator.innerHTML = '<hr class="flex-grow-1"><span class="mx-2 text-muted small">OR</span><hr class="flex-grow-1">';
    return separator;
  }

  checkForOAuthError() {
    const queryParams = this.appClient.layoutEngine.getQueryParams();
    const error = queryParams.error;

    if (error) {
      // Only show notifications for actual errors, not user cancellations
      if (error !== 'oauth_cancelled') {
        let errorMessage = 'Authentication failed. Please try again.';

        switch (error) {
          case 'oauth_failed':
            errorMessage = 'Authentication failed. Please try again or use a different sign-in method.';
            break;
          case 'oauth_error':
            errorMessage = 'An error occurred during authentication. Please try again.';
            break;
        }

        this.appClient.notifier.error(errorMessage, {
          title: 'Authentication Error',
          duration: 5000
        });
      }

      // Clean up the URL by removing the error parameter
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }

  async render() {
    this.container.innerHTML = '';

    const titleElement = document.createElement('h2');
    titleElement.className = 'card-title text-center fs-4 fw-bold mb-2';
    titleElement.textContent = this.t('Welcome Back');
    this.container.appendChild(titleElement);

    const oauthButtons = this._createOAuthProviderButtons();
    if (oauthButtons) {
      this.container.appendChild(oauthButtons);
    }

    if (this.emailLoginVisible) {
      if (oauthButtons) {
        this.container.appendChild(this._createSeparator());
      }
      const formElement = await this.formRenderer.render();
      this.formRenderer.on('formSubmit', this._boundHandleSubmit);
      this.container.appendChild(formElement);
    }

    return this.container;
  }

  async handleSubmit() {
    if (await this.formRenderer.isFormValid()) {
      const userData = this.formRenderer.getFormData();
      try {
        await this.sessionTokenHandler.login(userData.identifier, userData.password);
      } catch (apiError) {
        this.formRenderer.setSubmitting(false);
        this.appClient.notifier.error(apiError.message, { title: "Login Error" });
      }
    } else {
      this.log.warn("Submit blocked due to form validation errors.");
    }
  }

  async checkForOAuthPayload() {
    const queryParams = this.appClient.layoutEngine.getQueryParams();
    const encodedPayload = queryParams.oauth_payload;

    if (encodedPayload && !this.isProcessingOAuth) {
      // Immediately sanitize the browser address bar
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);

      this.isProcessingOAuth = true;
      this.renderLoadingState();

      this.appClient.onReady(async () => {
        try {
          // Validate payload format before decoding
          if (!/^[A-Za-z0-9+/=]+$/.test(encodedPayload)) {
            throw new Error("Invalid payload format");
          }

          const decodedString = atob(encodedPayload);

          // Validate JSON structure
          let sessionPayload;
          try {
            sessionPayload = JSON.parse(decodedString);
          } catch (parseError) {
            throw new Error("Invalid payload structure");
          }

          // Validate required fields
          if (!sessionPayload ||
            typeof sessionPayload !== 'object' ||
            !sessionPayload.user ||
            !sessionPayload.token ||
            typeof sessionPayload.user !== 'object' ||
            typeof sessionPayload.token !== 'string') {
            throw new Error("Decoded payload is invalid or missing required fields");
          }

          // Validate token format (should be a JWT or session token)
          if (sessionPayload.token.length < 20) {
            throw new Error("Token format invalid");
          }

          await this.appClient.sessionTokenHandler.processLoginSuccess(sessionPayload);

          // Clean up URL to remove sensitive data
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);

        } catch (error) {
          this.log.error("OAuth payload processing failed:", error);
          this.appClient.notifier.error("Authentication failed. The session data may be invalid.");
          this.isProcessingOAuth = false;

          // Clean up URL
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);

          this.appClient.layoutEngine.navigate(this.appClient.layoutEngine.defaultRoutes.login);
          this.render();
        }
      });
    }
  }

  async checkForOtlToken() {
    const queryParams = this.appClient.layoutEngine.getQueryParams();
    const token = queryParams.otl_token;

    if (token) {
      // Validate token format
      if (!/^[A-Za-z0-9_-]+$/.test(token)) {
        this.log.error("Invalid OTL token format");
        this.appClient.notifier.error("Invalid login link format.");
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
        return true;
      }

      this.renderLoadingState("Validating secure login link...");

      try {
        const sessionPayload = await this.appClient.rpcClient.call('AuthRpc.loginWithToken', { token });
        await this.appClient.sessionTokenHandler.processLoginSuccess(sessionPayload);

        // Clean up URL
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      } catch (error) {
        this.log.error("One-time-login failed:", error);
        this.appClient.notifier.error(error.message || "The login link is invalid or has expired.");

        // Clean up URL
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);

        this.appClient.layoutEngine.navigate(this.appClient.layoutEngine.defaultRoutes.login);
        this.render();
      }

      return true;
    }
    return false;
  }

  renderLoadingState(message = "Finalizing login...") {
    if (!this.container) return;
    this.container.innerHTML = `
      <div class="d-flex justify-content-center align-items-center" style="min-height: 300px;">
        <div class="text-center">
          <div class="spinner-border text-primary" role="status"></div>
          <p class="mt-3 text-muted">${this.t(message)}</p>
        </div>
      </div>
    `;
  }

  destroy() {
    // Clean up event listeners
    window.removeEventListener('message', this._boundHandleOAuthMessage);

    // Close any open OAuth windows
    for (const [provider, popup] of this.oauthWindows.entries()) {
      if (popup && !popup.closed) {
        popup.close();
      }
    }
    this.oauthWindows.clear();

    if (this.formRenderer) {
      this.formRenderer.off('formSubmit', this._boundHandleSubmit);
    }

    super.destroy();
  }
}
